import { IContactTag } from '@trii/types/dist/Contacts';
import { Box, Chip, IconButton, Popover, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LabelIcon from '@mui/icons-material/Label';
import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslationContext } from '../../../../context/TranslationContext';

interface TagsProps {
  tags: IContactTag[];
  onEditTags: () => void;
}

const Tags = ({ tags, onEditTags }: TagsProps) => {
  const t = useTranslationContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const safeTags = useMemo(() => tags ?? [], [tags]);
  const MAX_VISIBLE = 4;
  const visibleTags = safeTags.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(safeTags.length - visibleTags.length, 0);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        mt: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: 320,
          position: 'relative',
        }}
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
          {visibleTags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: 999,
                maxWidth: 140,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}

          {hiddenCount > 0 ? (
            <Chip
              label={`+${hiddenCount}`}
              size="small"
              variant="outlined"
              onClick={handleOpen}
              sx={{ borderRadius: 999 }}
            />
          ) : null}
        </Stack>

        <IconButton
          className="tags-action"
          size="small"
          aria-label={safeTags.length ? t('tags.edit') : t('tags.add')}
          onClick={onEditTags}
          sx={{
            position: 'absolute',
            left: '100%',
            ml: 1,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'text.secondary',
            //@ts-ignore
            bgcolor: (theme) => theme.palette.background.panel,
            transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
            '&:hover': {
              bgcolor: 'action.selected',
              color: 'text.primary',
            },
          }}
        >
          {safeTags.length ? <EditIcon fontSize="small" /> : <LabelIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 320, maxHeight: 240, overflowY: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('tags.label')}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {safeTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  maxWidth: 160,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default Tags;
